import { useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useQueryParam } from '@/hooks/useQueryParam';
import { mergeQueryParams } from '@/utils/queryParams';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import BuildStatus from '@/models/BuildStatus';
import { PipelineStatus } from '@/models/Pipeline';
import { TriggerTypes } from '@/models/Build';

export type BuildFilterName =
  | 'after_date'
  | 'artifact_type'
  | 'before_date'
  | 'branch'
  | 'pipeline'
  | 'search'
  | 'status'
  | 'trigger_event_type'
  | 'workflow';

export const allBuildFilters: BuildFilterName[] = [
  'after_date',
  'artifact_type',
  'before_date',
  'branch',
  'pipeline',
  'search',
  'status',
  'trigger_event_type',
  'workflow',
];

export type BuildFilterState = {
  artifact_type: string[];
  branch: string[];
  date_range: [string, string];
  pipeline: string[];
  search: string[];
  status: string[];
  trigger_event_type: string[];
  workflow: string[];
};

const useBuildFilterState = (
  updateFilters: (filters: BuildListFilters) => void,
): {
  setFilters: (filters: { key: BuildFilterName; val: string }[]) => void;
  state: Partial<BuildFilterState>;
} => {
  const afterDateFilter = useQueryParam('after_date', undefined) || null;
  const artifactFilter = useQueryParam('artifact_type', undefined) || null;
  const beforeDateFilter = useQueryParam('before_date', undefined) || null;
  const branchFilter = useQueryParam('branch', undefined) || null;
  const pipelineFilter = useQueryParam('pipeline', undefined) || null;
  const searchFilter = useQueryParam('search', '');
  const statusFilter = useQueryParam('status', undefined) || null;
  const triggerFilter = useQueryParam('trigger_event_type', undefined) || null;
  const workflowFilter = useQueryParam('workflow', undefined) || null;

  useEffect(() => {
    updateFilters({
      afterDate: afterDateFilter ? DateTime.fromMillis(Number(afterDateFilter)).toISO() : null,
      artifactType: artifactFilter,
      beforeDate: beforeDateFilter ? DateTime.fromMillis(Number(beforeDateFilter)).toISO() : null,
      branch: branchFilter,
      pipelineable: workflowFilter || pipelineFilter,
      status: statusFilter as BuildStatus | PipelineStatus | null,
      term: searchFilter,
      triggerType: triggerFilter as TriggerTypes | null,
    });
  }, [
    afterDateFilter,
    artifactFilter,
    beforeDateFilter,
    branchFilter,
    pipelineFilter,
    searchFilter,
    statusFilter,
    triggerFilter,
    updateFilters,
    workflowFilter,
  ]);

  const setFilters = (filters: { key: BuildFilterName; val: string }[]) => {
    const set = filters.filter(({ val }) => Boolean(val)).map(({ key, val }) => ({ type: key, value: val }));
    const unset = filters.filter(({ val }) => !val).map(({ key }) => key);

    mergeQueryParams({ set, unset });
  };

  const dateRangeFilter = useMemo(() => {
    return [afterDateFilter || '', beforeDateFilter || ''] as const;
  }, [afterDateFilter, beforeDateFilter]);

  return {
    setFilters,
    state: {
      artifact_type: artifactFilter ? [artifactFilter] : undefined,
      branch: branchFilter ? [branchFilter] : undefined,
      date_range: dateRangeFilter && [dateRangeFilter[0], dateRangeFilter[1]],
      pipeline: pipelineFilter ? [pipelineFilter] : undefined,
      search: searchFilter ? [searchFilter] : undefined,
      status: statusFilter ? [statusFilter] : undefined,
      trigger_event_type: triggerFilter ? [triggerFilter] : undefined,
      workflow: workflowFilter ? [workflowFilter] : undefined,
    },
  };
};

export default useBuildFilterState;
